import { BLANK_LIBRARY_TEMPLATE_IDS, type BlankLibraryTemplateId } from './canned/blankBoard'
import { defaultMapboxToken } from './maps'
import type { ScenarioSession } from './types'

export type BoardCamera = { lng: number; lat: number; zoom: number }

/**
 * Quiet continental-US framing before a place is picked.
 * Offset south of the geographic center so the label is not Lebanon, KS.
 */
export const BOARD_LOCATION_PREVIEW_IDLE: BoardCamera = {
  lng: -96.8,
  lat: 37.8,
  zoom: 3.3,
}

const IDLE_BY_TIMEZONE: Record<string, BoardCamera> = {
  'America/New_York': { lng: -76.5, lat: 40.2, zoom: 4.4 },
  'America/Chicago': { lng: -92.5, lat: 38.6, zoom: 4.2 },
  'America/Denver': { lng: -106.5, lat: 39.5, zoom: 4.4 },
  'America/Los_Angeles': { lng: -119.5, lat: 36.8, zoom: 4.4 },
  'America/Phoenix': { lng: -111.8, lat: 34.0, zoom: 5 },
}

/** Idle preview: browser timezone when we have a US region, else the lower 48. */
export function boardLocationPreviewIdle(timeZone?: string): BoardCamera {
  const zone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
  return IDLE_BY_TIMEZONE[zone] ?? BOARD_LOCATION_PREVIEW_IDLE
}

/**
 * Default zoom after a geocode pick — slightly wide so the place sits
 * in context without requiring the user to touch the slider.
 */
export const BOARD_LOCATION_DEFAULT_ZOOM = 15

/** Country-wide at the low end; neighborhood-tight at the high end. */
export const BOARD_LOCATION_ZOOM_MIN = 3
export const BOARD_LOCATION_ZOOM_MAX = 17

export type GeocodeHit = {
  id: string
  label: string
  lng: number
  lat: number
}

/** True when this library id is an empty type seed (Untitled whiteboard / walkthrough / exercise). */
export function isBlankBoardTemplateId(templateId: string | null | undefined): boolean {
  const id = templateId?.trim() ?? ''
  if (!id) return false
  return BLANK_LIBRARY_TEMPLATE_IDS.some((key) => id === key || id.endsWith('/' + key))
}

function blankLibraryKeyFromId(id: string): BlankLibraryTemplateId | null {
  for (const key of BLANK_LIBRARY_TEMPLATE_IDS) {
    if (id === key || id.endsWith('/' + key)) return key
  }
  return null
}

/**
 * Whether Set Location / board home is locked for this session.
 * Prefers the stamped boolean; legacy boards infer from blank-library lineage
 * (`instanceTemplateId` may be a catalog key or a template row Guid — pass the
 * resolved key when known).
 */
export function isBoardLocationLocked(
  session: Pick<ScenarioSession, 'boardLocationLocked' | 'id'>,
  instanceTemplateId?: string | null,
): boolean {
  if (typeof session.boardLocationLocked === 'boolean') {
    return session.boardLocationLocked
  }
  if (isBlankBoardTemplateId(instanceTemplateId) || isBlankBoardTemplateId(session.id)) {
    return false
  }
  // Unknown legacy provenance — do not offer relocating authored geography by accident.
  return true
}

/** Resolve library key for lock checks (Guid row id → blank-* when applicable). */
export function resolveTemplateKeyForLocation(
  instanceTemplateId: string | null | undefined,
  templates: readonly { id: string; session: { id: string } }[],
): string | null {
  const tid = instanceTemplateId?.trim()
  if (!tid) return null
  const blankKey = blankLibraryKeyFromId(tid)
  if (blankKey) return blankKey
  const tmpl = templates.find((t) => t.id === tid || t.session.id === tid)
  if (!tmpl) return tid
  const fromSession = blankLibraryKeyFromId(tmpl.session.id)
  if (fromSession) return fromSession
  return tmpl.session.id || tmpl.id
}

/** Stamp for a new board created from a library template. */
export function boardLocationLockedForTemplateId(templateId: string): boolean {
  return !isBlankBoardTemplateId(templateId)
}

/** Drop near-duplicate centers (address + POI + place for the same spot). */
export function dedupeGeocodeHits(hits: readonly GeocodeHit[]): GeocodeHit[] {
  const out: GeocodeHit[] = []
  for (const hit of hits) {
    const near = out.some(
      (prev) => Math.hypot(prev.lng - hit.lng, prev.lat - hit.lat) < 0.00035,
    )
    if (!near) out.push(hit)
  }
  return out
}

/**
 * Parse a typed lat/long pair so Set Location can skip geocoding.
 * Accepts `33.85, -115.48`, `[33.85, -115.48]`, optional N/S/E/W.
 * First number is latitude; if that is out of range and the pair looks like
 * lng/lat, the values swap.
 */
export function parseLatLngQuery(query: string): { lat: number; lng: number } | null {
  const raw = query.trim()
  if (!raw) return null
  const inner = raw.replace(/^\[/, '').replace(/\]$/, '').trim()
  const compact = inner.replace(/[°º]/g, ' ')
  if (/[A-Za-z]/.test(compact.replace(/[NSEW]/gi, ''))) return null
  const nums = compact.match(/[+-]?\d+(?:\.\d+)?/g)
  if (!nums || nums.length < 2) return null
  const looksPair =
    raw.startsWith('[') ||
    inner.includes(',') ||
    inner.includes(';') ||
    /[NSEW]/i.test(compact) ||
    (nums[0]!.includes('.') && nums[1]!.includes('.'))
  if (!looksPair) return null
  let lat = Number(nums[0])
  let lng = Number(nums[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  const hemi = compact.toUpperCase()
  if (/(?:^|[\s,;])S(?:[\s,;]|$)/.test(hemi) || /S\s*$/.test(hemi)) lat = -Math.abs(lat)
  if (/(?:^|[\s,;])N(?:[\s,;]|$)/.test(hemi)) lat = Math.abs(lat)
  if (/(?:^|[\s,;])W(?:[\s,;]|$)/.test(hemi) || /W\s*$/.test(hemi)) lng = -Math.abs(lng)
  if (/(?:^|[\s,;])E(?:[\s,;]|$)/.test(hemi)) lng = Math.abs(lng)
  if (Math.abs(lat) > 90 && Math.abs(lng) <= 90 && Math.abs(lat) <= 180) {
    const swap = lat
    lat = lng
    lng = swap
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}

export function latLngHit(lat: number, lng: number): GeocodeHit {
  return {
    id: `ll:${lat.toFixed(6)},${lng.toFixed(6)}`,
    label: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    lat,
    lng,
  }
}

export async function searchBoardLocations(
  query: string,
  opts?: { proximity?: { lng: number; lat: number }; signal?: AbortSignal },
): Promise<GeocodeHit[]> {
  const q = query.trim()
  if (q.length < 2) return []
  const token = defaultMapboxToken()
  if (!token) return []
  const params = new URLSearchParams({
    access_token: token,
    autocomplete: 'true',
    limit: '8',
    types: 'address,poi,place,locality,neighborhood',
  })
  if (opts?.proximity) {
    params.set('proximity', `${opts.proximity.lng},${opts.proximity.lat}`)
  }
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?${params}`
  const res = await fetch(url, { signal: opts?.signal })
  if (!res.ok) return []
  const body = (await res.json()) as {
    features?: { id?: string; place_name?: string; center?: [number, number] }[]
  }
  const hits: GeocodeHit[] = []
  for (const f of body.features ?? []) {
    const center = f.center
    if (!center || center.length < 2) continue
    const [lng, lat] = center
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue
    hits.push({
      id: f.id || `${lng},${lat}`,
      label: (f.place_name || '').trim() || `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      lng,
      lat,
    })
  }
  return dedupeGeocodeHits(hits).slice(0, 5)
}

export function cameraFromGeocodeHit(
  hit: GeocodeHit,
  zoom: number = BOARD_LOCATION_DEFAULT_ZOOM,
): BoardCamera {
  return { lng: hit.lng, lat: hit.lat, zoom }
}

/** True when two framings match closely enough to skip a hub write. */
export function boardCamerasEqual(a: BoardCamera, b: BoardCamera): boolean {
  return (
    Math.abs(a.lng - b.lng) < 1e-7 &&
    Math.abs(a.lat - b.lat) < 1e-7 &&
    Math.abs(a.zoom - b.zoom) < 1e-3
  )
}

/** Update session home camera and the selected plan version seed when present. */
export function applyBoardCameraToSession(
  session: ScenarioSession,
  camera: BoardCamera,
): ScenarioSession {
  const nextCamera = { ...camera }
  const versions = session.boardVersions
  if (!versions?.length) {
    return { ...session, camera: nextCamera }
  }
  const selectedId = session.selectedVersionId ?? versions[0]?.id
  return {
    ...session,
    camera: nextCamera,
    boardVersions: versions.map((v) =>
      v.id === selectedId ? { ...v, seed: { ...v.seed, camera: nextCamera } } : v,
    ),
  }
}
